import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PrescriptionTemplates = ({ onUseTemplate }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Templates', icon: 'Grid3X3' },
    { id: 'cardiovascular', name: 'Cardiovascular', icon: 'Heart' },
    { id: 'diabetes', name: 'Diabetes', icon: 'Activity' },
    { id: 'respiratory', name: 'Respiratory', icon: 'Wind' },
    { id: 'pain', name: 'Pain Management', icon: 'Zap' },
    { id: 'antibiotics', name: 'Antibiotics', icon: 'Shield' }
  ];

  const templates = [
    {
      id: 't001',
      name: 'Hypertension - Standard',
      category: 'cardiovascular',
      medication: 'Lisinopril',
      dosage: '10mg',
      frequency: 'once_daily',
      duration: '30 days',
      instructions: 'Take with or without food. Monitor blood pressure regularly.',
      usageCount: 45,
      lastUsed: '2025-09-20'
    },
    {
      id: 't002',
      name: 'Type 2 Diabetes - Initial',
      category: 'diabetes',
      medication: 'Metformin',
      dosage: '500mg',
      frequency: 'twice_daily',
      duration: '30 days',
      instructions: 'Take with meals to reduce stomach upset. Monitor blood glucose.',
      usageCount: 38,
      lastUsed: '2025-09-21'
    },
    {
      id: 't003',
      name: 'High Cholesterol',
      category: 'cardiovascular',
      medication: 'Atorvastatin',
      dosage: '20mg',
      frequency: 'once_daily',
      duration: '30 days',
      instructions: 'Take in the evening. Avoid grapefruit juice.',
      usageCount: 32,
      lastUsed: '2025-09-19'
    },
    {
      id: 't004',
      name: 'Asthma - Maintenance',
      category: 'respiratory',
      medication: 'Fluticasone/Salmeterol',
      dosage: '25/250mcg',
      frequency: 'twice_daily',
      duration: '30 days',
      instructions: 'Rinse mouth after use. Use spacer device.',
      usageCount: 28,
      lastUsed: '2025-09-18'
    },
    {
      id: 't005',
      name: 'Acute Pain - Short Term',
      category: 'pain',
      medication: 'Ibuprofen',
      dosage: '400mg',
      frequency: 'three_times_daily',
      duration: '7 days',
      instructions: 'Take with food. Do not exceed 7 days without medical review.',
      usageCount: 24,
      lastUsed: '2025-09-22'
    },
    {
      id: 't006',
      name: 'UTI - Standard Treatment',
      category: 'antibiotics',
      medication: 'Nitrofurantoin',
      dosage: '100mg',
      frequency: 'twice_daily',
      duration: '7 days',
      instructions: 'Take with food. Complete full course even if symptoms improve.',
      usageCount: 19,
      lastUsed: '2025-09-17'
    }
  ];

  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates?.filter(template => template?.category === selectedCategory);

  const handleUseTemplate = (template) => {
    onUseTemplate({
      medication: template?.medication,
      dosage: template?.dosage,
      frequency: template?.frequency,
      duration: template?.duration,
      instructions: template?.instructions
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-foreground flex items-center space-x-2">
          <Icon name="FileTemplate" size={20} />
          <span>Prescription Templates</span>
        </h3>
        <Button variant="outline" size="sm" iconName="Plus">
          Create Template
        </Button>
      </div>
      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories?.map((category) => (
          <button
            key={category?.id}
            onClick={() => setSelectedCategory(category?.id)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-clinical ${
              selectedCategory === category?.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
            }`}
          >
            <Icon name={category?.icon} size={16} />
            <span>{category?.name}</span>
          </button>
        ))}
      </div>
      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTemplates?.map((template) => (
          <div key={template?.id} className="bg-muted rounded-lg p-4 hover:shadow-clinical transition-clinical">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-medium text-foreground mb-1">{template?.name}</h4>
                <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                  <span>Used {template?.usageCount} times</span>
                  <span>•</span>
                  <span>Last: {template?.lastUsed}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center space-x-2">
                <Icon name="Pill" size={14} className="text-primary" />
                <span className="text-sm text-foreground">{template?.medication}</span>
                <span className="text-sm text-muted-foreground">({template?.dosage})</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={14} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {template?.frequency?.replace('_', ' ')} for {template?.duration}
                </span>
              </div>
            </div>

            <div className="bg-card rounded-md p-3 mb-4">
              <p className="text-xs text-muted-foreground line-clamp-2">
                {template?.instructions}
              </p>
            </div>

            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                template?.category === 'cardiovascular' ? 'bg-red-100 text-red-800' :
                template?.category === 'diabetes' ? 'bg-blue-100 text-blue-800' :
                template?.category === 'respiratory' ? 'bg-green-100 text-green-800' :
                template?.category === 'pain' ? 'bg-yellow-100 text-yellow-800' :
                template?.category === 'antibiotics'? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {categories?.find(cat => cat?.id === template?.category)?.name}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                iconName="Copy"
                onClick={() => handleUseTemplate(template)}
              >
                Use
              </Button>
            </div>
          </div>
        ))}
      </div>
      {filteredTemplates?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No templates found</h3>
          <p className="text-muted-foreground mb-4">
            No templates available for the selected category.
          </p>
          <Button variant="outline" iconName="Plus">
            Create New Template
          </Button>
        </div>
      )}
    </div>
  );
};

export default PrescriptionTemplates;